import { Box, Button, FormControl, Grid, InputLabel, Link, ListSubheader, MenuItem, OutlinedInput, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridColumnVisibilityModel, GridRenderCellParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { RootState } from '../../reducers';
import {
    fetchClaimsData,
    fetchClaimsByClaimNumber,
    fetchClaimResponses,
    setClaimNumberSearchValue,
    setProviderClaimSelectedStatus,
    setDosStart,
    setDosEnd,
    setProviderClaimsSelectedProviderId,
    exportClaimsData,
} from '../../store/providerClaims.slice';
import Loading from '../Shared/Loading';
import { parentUrl } from '../../utils/constants';
import MessageDisplay from '../Shared/MessageDisplay';
import Authorization from '../../utils/Authorization';
import ClaimEditResubmission from './ClaimEditResubmission';
import { fetchClaimResubmitEditData, setShowEditResubmitModal } from '../../store/claimResubmission.slice';
import ProcedureSignOffEncounter from '../Assessments/ProcedureSignOff/ProcedureSignOffEncounter';
import { fetchEncountersByDate, setEncounterChange, setShowProcedureSignOffModal } from '../../store/procedureSignOffEncounter.slice';
import { getStatuses, setShowClaimHistoryModal, statusValues } from '../../store/claimsManagement.slice';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import ClaimHistoryModal from './ClaimHistoryModal';
import { getExportModels } from '../../utils/exportUtils';

const ProviderClaims = () => {
    const authData = new Authorization();
    const { claimsData, claimNumberSearchValue, providerClaimSelectedStatus, dosStart, dosEnd, selectedProviderId } = useSelector(
        (state: RootState) => state.providerClaims
    );
    const { encounterChange } = useSelector((state: RootState) => state.procedureSignOffEncounter);
    const { encounterClaimProviders } = useSelector((state: RootState) => state.shared);
    const dispatch = useAppDispatch();
    const [claimResponseButtonDisplay, setClaimResponseButtonDisplay] = useState(false);
    const [localProviders, setLocalProviders] = useState([]);

    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [selectedClaim, setSelectedClaim] = useState<any>();
    const [paginationModel, setPaginationModel] = useState({ pageSize: 15, page: 0 });

    const diagnosisFormatHandler = (params: GridRenderCellParams<any>) => {
        return params.value ? (
            <Stack>
                <Typography>{params.value.Code} -</Typography>
                <Typography color={'#999'}>{params.value.Name}</Typography>
            </Stack>
        ) : (
            <Typography></Typography>
        );
    };

    const showEditResubmit = (claimNumber: string, memberId: string, dateOfService: string) => {
        dispatch(
            fetchClaimResubmitEditData(claimNumber, memberId, dateOfService, () => {
                dispatch(setShowEditResubmitModal(true));
            })
        );
    };

    const showProcedureSignOffEncounter = (placeOfService: number, status: string, memberId: string, providerId: string, dateOfService: string, engagementId?: string) => {
        dispatch(
            fetchEncountersByDate(placeOfService, status, memberId, providerId, dateOfService, () => {
                dispatch(setShowProcedureSignOffModal(true));
            }, engagementId)
        );
    };

    const columns: GridColDef[] = [
        {
            field: 'Edit',
            headerName: 'Edit',
            minWidth: 130,
            flex: 0.75,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Button
                        className="button-120"
                        variant="contained"
                        onClick={() => {
                            showProcedureSignOffEncounter(
                                params.row.PlaceOfService,
                                params.row.Status,
                                params.row.MemberId,
                                params.row.CreatedById,
                                params.row.DateOfService,
                                params.row.EngagementId
                            );
                        }}
                    >
                        Edit Encounter
                    </Button>
                );
            },
        },
        {
            field: 'EditClaim',
            headerName: 'Edit',
            minWidth: 130,
            flex: 0.75,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Button
                        className="button-120"
                        variant="contained"
                        disabled={(params.row.CHStatus !== 1 && params.row.CHStatus < 4) || params.row.CHStatus === 12}
                        onClick={() => {
                            showEditResubmit(params.row.TraceId, params.row.MemberId, params.row.DateOfService);
                        }}
                    >
                        Modify Claim
                    </Button>
                );
            },
        },
        {
            field: 'IsModified',
            headerName: 'Modified',
            minWidth: 80,
            flex: 0.75,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    params.row.IsModified && (
                        <ContentPasteSearchIcon
                            className="icon-claim-history"
                            onClick={() => {
                                setSelectedClaim(params.row);
                                console.log('Selected Claim:', params.row);
                                dispatch(setShowClaimHistoryModal(true));
                            }}
                        />
                    )
                );
            },
        },
        {
            field: 'DateProcessed',
            headerName: 'Date Processed',
            flex: 0.75,
        },
        {
            field: 'TraceId',
            headerName: 'Claim Number',
            flex: 1.35,
        },
        {
            field: 'EncounterStatusValue',
            headerName: 'Status',
            flex: 0.75,
        },
        {
            field: 'Message',
            headerName: 'Last Message',
            flex: 1.25,
        },
        {
            field: 'DateOfService',
            headerName: 'DOS',
            flex: 0.75,
        },
        {
            field: 'AHCCCSId',
            headerName: 'AHCCCS ID',
            flex: 0.75,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            minWidth: 200,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link
                        target={'_blank'}
                        underline="hover"
                        href={`${parentUrl}provider/members-list/member-chart/${params.row.MemberId}/member-profile`}
                        color={'#337ab7'}
                    >
                        {params.value}
                    </Link>
                );
            },
        },
        {
            field: 'ContractingHealthPlan',
            headerName: 'Contracting Health Plan',
            flex: 1,
        },
        {
            field: 'CreatedBy',
            headerName: 'Creating Provider',
            flex: 1,
        },
        {
            field: 'CreatedDate',
            headerName: 'Created Date',
            flex: 1,
        },
        {
            field: 'ApprovedBy',
            headerName: 'Submitting Provider',
            flex: 1,
        },
        {
            field: 'ApprovedDate',
            headerName: 'Submitted Date',
            flex: 1,
        },
        {
            field: 'CPTCode',
            headerName: 'CPT Code',
            flex: 0.75,
        },
        {
            field: 'PlaceOfService',
            headerName: 'Place of Service',
            flex: 0.75,
        },
        {
            field: 'PrimaryDiagnosis',
            headerName: 'Primary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'SecondaryDiagnosis',
            headerName: 'Secondary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'TertiaryDiagnosis',
            headerName: 'Tertiary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'QuaternaryDiagnosis',
            headerName: 'Quaternary DX',
            flex: 0.75,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'CHPErrorMessage',
            headerName: 'CHP Error Message',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ maxWidth: '100%' }}>
                        <Typography sx={{ color: 'error.main' }}>{params.value}</Typography>
                    </div>
                );
            },
        },
        {
            field: 'CHPErrorMessageDate',
            headerName: 'CHP Error Date',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ maxWidth: '100%' }}>
                        <Typography sx={{ color: 'error.main' }}>{params.value}</Typography>
                    </div>
                );
            },
        },
        {
            field: 'MessageWaystar',
            headerName: 'Last Message',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ maxWidth: '100%' }}>
                        <Typography sx={{ color: 'error.main' }}>{params.row.Message}</Typography>
                    </div>
                );
            },
        },
    ];

    const baseColumns = {
        EncounterStatusValue: true,
        DateOfService: true,
        AHCCCSId: true,
        MemberName: true,
        ContractingHealthPlan: true,
        CreatedDate: true,
        CreatedBy: true,
        CPTCode: true,
        PlaceOfService: true,
    };

    const forReviewColumnModel = {
        ...baseColumns,
        Edit: true,
        PrimaryDiagnosis: true,
        SecondaryDiagnosis: true,
        TertiaryDiagnosis: true,
        QuaternaryDiagnosis: true,
        ApprovedBy: false,
        ApprovedDate: false,
        DateProcessed: false,
        TraceId: false,
        EncounterId: false,
        EditClaim: false,
        CHPErrorMessage: false,
        CHPErrorMessageDate: false,
        MessageWaystar: false,
        IsModified: false,
    };

    const toSubmitColumnModel = {
        ...forReviewColumnModel,
        EncounterId: true,
        TraceId: false,
        CreatedBy: false,
        CreatedDate: false,
        ApprovedBy: true,
        ApprovedDate: true,
        CHPErrorMessage: true,
        CHPErrorMessageDate: true,
        MessageWaystar: false,
        IsModified: false,
    };

    const acceptedClaimModel = {
        ...baseColumns,
        EncounterId: false,
        TraceId: true,
        PrimaryDiagnosis: false,
        SecondaryDiagnosis: false,
        TertiaryDiagnosis: false,
        QuaternaryDiagnosis: false,
        ApprovedBy: false,
        ApprovedDate: false,
        EditClaim: true,
        Edit: false,
        CHPErrorMessage: false,
        CHPErrorMessageDate: false,
        MessageWaystar: false,
        IsModified: true,
    };

    const rejectedColumnModel = {
        ...acceptedClaimModel,
        EncounterId: true,
        Edit: false,
        MessageWaystar: true,
    };

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(forReviewColumnModel);

    const changeHeaders = (paramStatus: string = null) => {
        if (!paramStatus) {
            paramStatus = providerClaimSelectedStatus;
        }

        const selectedManagementStatus = statusValues.find((x) => x.key === paramStatus);
        setClaimResponseButtonDisplay(false);
        if (selectedManagementStatus.value === 1 && selectedManagementStatus.type === 'Encounter') {
            setColumnVisibilityModel(forReviewColumnModel);
        } else if (selectedManagementStatus.value === 2) {
            setColumnVisibilityModel(toSubmitColumnModel);
        } else if (
            (selectedManagementStatus.value === 1 && selectedManagementStatus.type === 'Claim') ||
            selectedManagementStatus.value === 4 ||
            selectedManagementStatus.value === 8 ||
            selectedManagementStatus.value === 9 ||
            selectedManagementStatus.value === 10 ||
            selectedManagementStatus.value === 13
        ) {
            setColumnVisibilityModel(acceptedClaimModel);
        } else if (
            selectedManagementStatus.value === 5 ||
            selectedManagementStatus.value === 6 ||
            selectedManagementStatus.value === 7 ||
            selectedManagementStatus.value === 11 ||
            selectedManagementStatus.value === 12
        ) {
            setColumnVisibilityModel(rejectedColumnModel);
        }
    };

    const searchByClaimNumber = (event: any) => {
        if (!event.key || event.key === 'Enter') {
            setLoadingMessage('Loading...');
            dispatch(fetchClaimsByClaimNumber(selectedProviderId, claimNumberSearchValue));
        }
    };

    const getClaimResponseHandler = () => {
        setLoadingMessage('Downloading Response Files...');
        dispatch(fetchClaimResponses(searchClaims));
    };

    const changeProvider = (providerId: string) => {
        dispatch(setProviderClaimsSelectedProviderId(providerId));
    };

    const searchClaims = () => {
        if (claimNumberSearchValue) {
            searchByClaimNumber({});
        } else {
            setLoadingMessage('Loading...');
            dispatch(fetchClaimsData(selectedProviderId, providerClaimSelectedStatus, dosStart, dosEnd));
            changeHeaders();
        }
    };

    const exportClaims = () => {
        setLoadingMessage('Loading...');
        const exportModels = getExportModels(columns, columnVisibilityModel);
        dispatch(exportClaimsData(selectedProviderId, providerClaimSelectedStatus, dosStart, dosEnd, exportModels, claimNumberSearchValue));
    };

    useEffect(() => {
        if (authData.isInPermission('View Claims Management')) {
            setLocalProviders(encounterClaimProviders);
        } else {
            setLocalProviders(encounterClaimProviders.filter((x) => x.Id === authData.UserId));
            dispatch(setProviderClaimsSelectedProviderId(authData.UserId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [encounterClaimProviders]);

    useEffect(() => {
        if (encounterChange) {
            searchClaims();
            dispatch(setEncounterChange(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, encounterChange]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MessageDisplay />
            <ClaimEditResubmission encounterStatus={statusValues.find((x) => x.key === providerClaimSelectedStatus).value} searchClaims={searchClaims} />
            <ProcedureSignOffEncounter encounterStatus={statusValues.find((x) => x.key === providerClaimSelectedStatus).value} searchClaims={searchClaims} />
            <ClaimHistoryModal claim={selectedClaim} />
            <Box>
                {<Loading message={loadingMessage} />}
                {/* <Grid
                    container
                    justifyContent={'flex-end'}
                    spacing={3}
                    style={{ padding: '16px 16px 16px 16px' }}
                > */}
                <Grid container justifyContent={'flex-end'} spacing={3} style={{ padding: '16px 16px 16px 16px' }}>
                    <Grid size={2} textAlign={'left'}>
                        <Typography variant="h5">
                            {statusValues.find((x) => x.key === providerClaimSelectedStatus).type === 'Claim' ? 'Claims' : 'Member Encounters'}
                        </Typography>
                        <Typography
                            variant="h6"
                            className={statusValues.find((x) => x.key === providerClaimSelectedStatus).value > 2 ? 'colorsuccess' : 'colorerror'}
                        >
                            {statusValues.find((x) => x.key === providerClaimSelectedStatus).value > 2 ? 'Exist in Waystar' : 'Not in Waystar'}
                        </Typography>
                        <br />
                        <Stack direction="column" alignItems={'left'} justifyContent={'flex-end'} spacing={3}>
                            {claimResponseButtonDisplay && (
                                <Button
                                    onClick={getClaimResponseHandler}
                                    style={{
                                        minWidth: '120px!important',
                                        marginRight: 'auto',
                                    }}
                                    variant="contained"
                                >
                                    Get Claim Responses
                                </Button>
                            )}
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Select Provider</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Select Provider"
                                    disabled={!authData.isInPermission('View Claims Management')}
                                    value={selectedProviderId}
                                    onChange={(e) => changeProvider(e.target.value)}
                                >
                                    <MenuItem key={''} value={''}>
                                        {`Select A Provider`}
                                    </MenuItem>
                                    {localProviders.map(function (provider) {
                                        return (
                                            <MenuItem key={provider.Id} value={provider.Id}>
                                                {`${provider.LastName}, ${provider.FirstName}`}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            {statusValues.find((x) => x.key === providerClaimSelectedStatus).type === 'Claim' ? (
                                <>
                                    <FormControl>
                                        <InputLabel htmlFor="outlined-adornment-password">Search By Claim Number</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-password"
                                            type="text"
                                            value={claimNumberSearchValue}
                                            onChange={(e) => {
                                                dispatch(setClaimNumberSearchValue(e.target.value as string));
                                            }}
                                            onKeyUp={(event: any) => {
                                                if (event.key === 'Enter') {
                                                    searchByClaimNumber(event);
                                                }
                                            }}
                                            label="Search By Claim Number"
                                        />
                                    </FormControl>
                                    <Typography>OR</Typography>
                                </>
                            ) : null}
                            <FormControl>
                                <InputLabel id="demo-simple-select-label">Status</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={providerClaimSelectedStatus}
                                    label="Status"
                                    onChange={(e) => {
                                        dispatch(setClaimNumberSearchValue(''));
                                        dispatch(setProviderClaimSelectedStatus(e.target.value));
                                    }}
                                >
                                    {getStatuses().map((statusByType, index) => [
                                        <ListSubheader key={index}>{statusByType.Name}</ListSubheader>,
                                        statusByType.List.map((status) => {
                                            return (
                                                <MenuItem key={status.key} value={status.key} sx={{ paddingLeft: 4 }}>
                                                    {status.label}
                                                </MenuItem>
                                            );
                                        }),
                                    ])}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <DesktopDatePicker
                                    label="DOS Start Date:"
                                    value={dosStart}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => {
                                        setClaimNumberSearchValue('');
                                        dispatch(setDosStart(newValue));
                                    }}
                                    slotProps={{ textField: {} }}
                                />
                            </FormControl>
                            <FormControl>
                                <DesktopDatePicker
                                    label="DOS End Date:"
                                    value={dosEnd}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => {
                                        setClaimNumberSearchValue('');
                                        dispatch(setDosEnd(newValue));
                                    }}
                                    slotProps={{ textField: {} }}
                                />
                            </FormControl>
                            <Button onClick={searchClaims} style={{ minWidth: '120px!important' }} variant="contained">
                                Search
                            </Button>
                            <Button
                                disabled={!claimsData || claimsData.length === 0}
                                onClick={exportClaims}
                                style={{ minWidth: '120px!important' }}
                                variant="contained"
                            >
                                Export
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid size={10}>
                        <div style={{ height: '88vh', width: '100%' }}>
                            <DataGrid
                                columnVisibilityModel={columnVisibilityModel}
                                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                                rows={claimsData}
                                columns={columns}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[5]}
                                getRowId={(row) => row.EncounterId}
                                getRowHeight={() => 'auto'}
                                sx={{
                                    [`& .${gridClasses.cell}`]: {
                                        py: 2,
                                    },
                                    '& .MuiDataGrid-columnHeaderTitleContainerContent .MuiCheckbox-root': {
                                        // display: 'none',
                                    },
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default ProviderClaims;
